import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useShop } from "../context/ShopContext";
import { ShoppingCart, Plus, Minus, Trash2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { createOrder, fetchPaymentConfig, createPaymentOrder, verifyPayment } from "../api";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const CartDrawer = () => {
  const {
    cart,
    cartOpen,
    setCartOpen,
    updateQty,
    removeFromCart,
    clearCart,
    cartTotal,
  } = useShop();

  const orderPayload = () => ({
    items: cart.map((i) => ({
      id: i.id,
      name: i.name,
      brand: i.brand || "",
      price: i.price,
      qty: i.qty,
      img: i.img || "",
    })),
    total: Number(cartTotal.toFixed(2)),
  });

  const finishOrder = (orderId, label = "placed") => {
    toast.success(`Order ${orderId} ${label} successfully!`);
    clearCart();
    setCartOpen(false);
  };

  const checkout = async () => {
    const payload = orderPayload();
    try {
      const cfg = await fetchPaymentConfig();
      if (cfg?.enabled) {
        const ok = await loadRazorpayScript();
        if (!ok) throw new Error("rzp-script");
        const razorOrder = await createPaymentOrder(payload.total);
        const rzp = new window.Razorpay({
          key: cfg.key_id,
          amount: razorOrder.amount,
          currency: "INR",
          name: "ArogyaMeds",
          description: "Medicines & Health Products",
          order_id: razorOrder.id,
          theme: { color: "#12a150" },
          handler: async (resp) => {
            try {
              const res = await verifyPayment({ ...resp, ...payload });
              finishOrder(res.order_id, "paid & placed");
            } catch {
              toast.error("Payment verification failed. Contact support.");
            }
          },
        });
        rzp.open();
        return;
      }
      // Payments not configured yet -> direct order (no payment collected)
      const res = await createOrder(payload);
      finishOrder(res.order_id);
    } catch {
      toast.error("Could not place order. Please try again.");
    }
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="w-[360px] sm:w-[400px] p-0 flex flex-col">
        <SheetHeader className="bg-[#0f9d4b] px-5 py-4">
          <SheetTitle className="text-white flex items-center gap-2 text-[16px]">
            <ShoppingCart size={19} /> My Cart
            <span className="text-white/80 text-[12px] font-normal">
              ({cart.length} items)
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-3">
                <ShoppingCart size={26} className="text-[#12a150]" />
              </div>
              <div className="text-[15px] font-bold text-gray-800">Your cart is empty</div>
              <div className="text-[12px] text-gray-400 mt-1">
                Add medicines & health products to get started
              </div>
              <button
                onClick={() => setCartOpen(false)}
                className="mt-4 bg-[#12a150] hover:bg-[#0d8a40] text-white text-[12.5px] font-semibold rounded-full px-5 py-2 transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            cart.map((item) => (
              <div
                key={item.id}
                className="flex gap-3 bg-white border border-gray-100 rounded-xl p-3 shadow-sm"
              >
                <img
                  src={item.img}
                  alt={item.name}
                  className="w-14 h-14 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-gray-800 truncate">
                    {item.name}
                  </div>
                  <div className="text-[11px] text-gray-400">{item.brand}</div>
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="flex items-center gap-2 border border-gray-200 rounded-full px-1 py-0.5">
                      <button
                        onClick={() => updateQty(item.id, -1)}
                        className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-[#0e7a3d]"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-[12px] font-bold w-4 text-center">{item.qty}</span>
                      <button
                        onClick={() => updateQty(item.id, 1)}
                        className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-[#0e7a3d]"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <span className="text-[13px] font-bold text-[#0e7a3d]">
                      ₹ {(item.price * item.qty).toFixed(2)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => removeFromCart(item.id)}
                  className="self-start text-gray-300 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-gray-100 p-4 space-y-2 bg-gray-50/50">
            <div className="flex justify-between text-[13px] text-gray-600">
              <span>Subtotal</span>
              <span className="font-semibold">₹ {cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-[13px] text-gray-600">
              <span>Delivery</span>
              <span className="font-semibold text-[#0e7a3d] flex items-center gap-1">
                <BadgeCheck size={14} /> FREE
              </span>
            </div>
            <div className="flex justify-between text-[15px] font-bold text-gray-800 pt-1 border-t border-dashed border-gray-200">
              <span>Total</span>
              <span>₹ {cartTotal.toFixed(2)}</span>
            </div>
            <button
              onClick={checkout}
              className="w-full bg-[#12a150] hover:bg-[#0d8a40] text-white text-[14px] font-bold rounded-full py-2.5 transition-colors"
            >
              Proceed to Checkout
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
